/**
 * EditableTableCell Component
 *
 * Generic inline editing component for table cells.
 * Supports text inputs, dropdowns, and time inputs with keyboard navigation.
 *
 * Features:
 * - Click to edit
 * - Enter to save, Escape to cancel
 * - Blur to save
 * - Smooth transitions
 * - Edit indicator on hover
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';

// Edit icon (pencil)
const EditIcon = () => (
  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

// Checkmark icon for save
const CheckIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

// X icon for cancel
const XIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export type CellType = 'text' | 'select' | 'time' | 'date';

export interface SelectOption {
  value: string;
  label: string;
  color?: string;
}

interface EditableTableCellProps {
  value: string | null;
  type: CellType;
  options?: SelectOption[];
  placeholder?: string;
  onSave: (value: string | null) => void;
  className?: string;
  displayValue?: React.ReactNode;
  emptyDisplay?: React.ReactNode;
}

export const EditableTableCell: React.FC<EditableTableCellProps> = ({
  value,
  type,
  options = [],
  placeholder = 'Enter value...',
  onSave,
  className = '',
  displayValue,
  emptyDisplay = '-',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (type === 'text' && inputRef.current instanceof HTMLInputElement) {
        inputRef.current.select();
      }
    }
  }, [isEditing, type]);

  // Reset edit value when value prop changes
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value || '');
    }
  }, [value, isEditing]);

  const handleStartEdit = useCallback(() => {
    setEditValue(value || '');
    setIsEditing(true);
  }, [value]);

  const handleSave = useCallback(() => {
    const newValue = editValue.trim() || null;
    if (newValue !== value) {
      onSave(newValue);
    }
    setIsEditing(false);
  }, [editValue, value, onSave]);

  const handleCancel = useCallback(() => {
    setEditValue(value || '');
    setIsEditing(false);
  }, [value]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  }, [handleSave, handleCancel]);

  const handleBlur = useCallback((e: React.FocusEvent) => {
    // Check if focus moved to a child element (like action buttons)
    if (containerRef.current?.contains(e.relatedTarget as Node)) {
      return;
    }
    handleSave();
  }, [handleSave]);

  // Render display mode
  if (!isEditing) {
    return (
      <div
        ref={containerRef}
        className={`hmc-editable-cell ${className}`}
        onClick={handleStartEdit}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleStartEdit()}
        title="Click to edit"
      >
        <div className="hmc-editable-display">
          {displayValue || value || emptyDisplay}
        </div>
        <div className={`hmc-editable-indicator ${isHovered ? 'hmc-editable-indicator-visible' : ''}`}>
          <EditIcon />
        </div>
      </div>
    );
  }

  // Render edit mode
  return (
    <div ref={containerRef} className={`hmc-editable-cell hmc-editable-cell-editing ${className}`}>
      {type === 'select' ? (
        <select
          ref={inputRef as React.RefObject<HTMLSelectElement>}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className="hmc-editable-select"
        >
          <option value="">-- Select --</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : type === 'time' ? (
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder="7:30 PM"
          className="hmc-editable-input hmc-editable-time"
        />
      ) : type === 'date' ? (
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type="date"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className="hmc-editable-input hmc-editable-date"
        />
      ) : (
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={placeholder}
          className="hmc-editable-input"
        />
      )}
      <div className="hmc-editable-actions">
        <button
          type="button"
          onClick={handleSave}
          className="hmc-editable-btn hmc-editable-btn-save"
          title="Save (Enter)"
        >
          <CheckIcon />
        </button>
        <button
          type="button"
          onClick={handleCancel}
          className="hmc-editable-btn hmc-editable-btn-cancel"
          title="Cancel (Escape)"
        >
          <XIcon />
        </button>
      </div>
    </div>
  );
};

export default EditableTableCell;
